import React from 'react';

import { faBuilding, faCalendar, faTag } from '@fortawesome/pro-regular-svg-icons';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { Icon } from '../Icon';

// Types
export interface PatientInfo {
  firstName?: string;
  lastName?: string;
  unosID?: string;
  patientNumber?: string; // MRN
  dob?: string | Date;
  age?: number;
  hospital?: string;
  gender?: string;
  caseID?: string;
}

export interface PatientInfoHeaderProps {
  patient?: PatientInfo | null;
  layout?: 'horizontal' | 'vertical' | 'compact';
  showFields?: {
    name?: boolean;
    unosID?: boolean;
    patientNumber?: boolean;
    age?: boolean;
    dob?: boolean;
    hospital?: boolean;
    gender?: boolean;
    caseID?: boolean;
  };
  variant?: 'primary' | 'secondary' | 'tertiary';
  className?: string;
  patientLabel?: string; // e.g., "Patient" or "Donor"
}

// Utility functions
const formatName = (firstName?: string, lastName?: string): string => {
  if (!firstName && !lastName) return 'Unknown Patient';
  return `${lastName || ''}, ${firstName || ''}`.replace(/^,\s*|,\s*$/g, '');
};

const calculateAge = (dob?: string | Date): number | null => {
  if (!dob) return null;
  const dobDate = dayjs(dob);
  if (!dobDate.isValid()) return null;
  return dayjs().diff(dobDate, 'year');
};

const formatDobWithAge = (dob?: string | Date, age?: number): string => {
  if (!dob) return 'Unknown';

  const dobDate = dayjs(dob);
  if (!dobDate.isValid()) return 'Invalid Date';

  const formattedDob = dobDate.format('MM/DD/YYYY');
  const calculatedAge = age ?? calculateAge(dob);

  return calculatedAge !== null ? `${formattedDob} (${calculatedAge})` : formattedDob;
};

// Default field visibility
const DEFAULT_FIELDS = {
  name: true,
  unosID: true,
  patientNumber: true,
  age: true,
  dob: true,
  hospital: true,
  gender: false,
  caseID: false,
};

// Styled Components
const HeaderContainer = styled.div<{
  layout: 'horizontal' | 'vertical' | 'compact';
  variant: 'primary' | 'secondary' | 'tertiary';
}>`
  display: ${({ layout }) => {
    switch (layout) {
      case 'horizontal':
        return 'grid';
      case 'vertical':
        return 'flex';
      case 'compact':
        return 'flex';
      default:
        return 'grid';
    }
  }};

  ${({ layout }) => {
    switch (layout) {
      case 'horizontal':
        return `
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem 2rem;
          padding: 1rem 1.5rem;
        `;
      case 'vertical':
        return `
          flex-direction: column;
          gap: 0.5rem;
          padding: 1rem;
        `;
      case 'compact':
        return `
          flex-wrap: wrap;
          gap: 0.75rem;
          padding: 0.75rem 1rem;
          align-items: center;
        `;
      default:
        return '';
    }
  }}

  background: ${({ theme, variant }) => {
    switch (variant) {
      case 'primary':
        return `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.palette.blues[6]} 100%)`;
      case 'secondary':
        return theme.colors.palette.blues[0];
      case 'tertiary':
        return theme.colors.palette.grayscale[0];
      default:
        return theme.colors.primary;
    }
  }};

  color: ${({ theme, variant }) => (variant === 'tertiary' ? theme.colors.palette.grayscale[7] : theme.colors.palette.white)};

  border-radius: ${({ variant }) => (variant === 'primary' ? '0' : '8px')};
  border: ${({ theme, variant }) => (variant === 'tertiary' ? `1px solid ${theme.colors.palette.grayscale[3]}` : 'none')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    padding: 1rem;
  }
`;

const InfoField = styled.div<{ layout: 'horizontal' | 'vertical' | 'compact' }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${({ layout }) => {
    switch (layout) {
      case 'horizontal':
        return `
          flex-direction: column;
          align-items: flex-start;
          min-width: 0;
        `;
      case 'vertical':
        return `
          flex-direction: row;
          flex-wrap: wrap;
        `;
      case 'compact':
        return `
          flex-direction: row;
          flex-shrink: 0;
        `;
      default:
        return '';
    }
  }}
`;

const FieldLabel = styled.span<{ layout: 'horizontal' | 'vertical' | 'compact' }>`
  font-size: ${({ layout }) => (layout === 'compact' ? '0.75rem' : '0.8rem')};
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  opacity: 0.9;
  ${({ layout }) => layout === 'horizontal' && 'margin-bottom: 0.25rem;'}
  white-space: nowrap;
`;

const FieldValue = styled.span<{ layout: 'horizontal' | 'vertical' | 'compact' }>`
  font-size: ${({ layout }) => {
    switch (layout) {
      case 'horizontal':
        return '1rem';
      case 'compact':
        return '0.875rem';
      default:
        return '0.9rem';
    }
  }};
  font-weight: ${({ layout }) => (layout === 'horizontal' ? '500' : '400')};
  word-break: break-word;
  flex: ${({ layout }) => (layout === 'vertical' ? '1' : 'none')};
`;

const FieldIcon = styled(Icon)`
  opacity: 0.8;
  font-size: 0.875rem;
`;

// Component
export const PatientInfoHeader: React.FC<PatientInfoHeaderProps> = ({
  patient,
  layout = 'horizontal',
  showFields = DEFAULT_FIELDS,
  variant = 'primary',
  className,
  patientLabel = 'Patient',
}) => {
  const fields = { ...DEFAULT_FIELDS, ...showFields };

  if (!patient) {
    return (
      <HeaderContainer layout={layout} variant={variant} className={className}>
        <InfoField layout={layout}>
          <FieldLabel layout={layout}>NO {patientLabel.toUpperCase()} SELECTED</FieldLabel>
        </InfoField>
      </HeaderContainer>
    );
  }

  const name = formatName(patient.firstName, patient.lastName);
  const nameWithGender = patient.gender ? `${name} (${patient.gender})` : name;
  const dobWithAge = formatDobWithAge(patient.dob, patient.age);

  return (
    <HeaderContainer layout={layout} variant={variant} className={className}>
      {fields.name && (
        <InfoField layout={layout}>
          {layout !== 'compact' && <FieldLabel layout={layout}>Name {patient.gender ? '(Gender)' : ''}:</FieldLabel>}
          <FieldValue layout={layout}>{nameWithGender}</FieldValue>
        </InfoField>
      )}

      {fields.unosID && patient.unosID && (
        <InfoField layout={layout}>
          {layout !== 'compact' && (
            <>
              <FieldIcon icon={faTag} />
              <FieldLabel layout={layout}>UNOS ID:</FieldLabel>
            </>
          )}
          <FieldValue layout={layout}>{patient.unosID}</FieldValue>
        </InfoField>
      )}

      {fields.patientNumber && patient.patientNumber && (
        <InfoField layout={layout}>
          {layout !== 'compact' && (
            <>
              <FieldIcon icon={faTag} />
              <FieldLabel layout={layout}>MRN/{patientLabel.toUpperCase()} ID:</FieldLabel>
            </>
          )}
          <FieldValue layout={layout}>{patient.patientNumber}</FieldValue>
        </InfoField>
      )}

      {(fields.dob || fields.age) && (
        <InfoField layout={layout}>
          {layout !== 'compact' && (
            <>
              <FieldIcon icon={faCalendar} />
              <FieldLabel layout={layout}>DOB (Age):</FieldLabel>
            </>
          )}
          <FieldValue layout={layout}>{dobWithAge}</FieldValue>
        </InfoField>
      )}

      {fields.hospital && patient.hospital && (
        <InfoField layout={layout}>
          {layout !== 'compact' && (
            <>
              <FieldIcon icon={faBuilding} />
              <FieldLabel layout={layout}>Hospital:</FieldLabel>
            </>
          )}
          <FieldValue layout={layout}>{patient.hospital}</FieldValue>
        </InfoField>
      )}

      {fields.caseID && patient.caseID && (
        <InfoField layout={layout}>
          {layout !== 'compact' && <FieldLabel layout={layout}>Case ID:</FieldLabel>}
          <FieldValue layout={layout}>{patient.caseID}</FieldValue>
        </InfoField>
      )}
    </HeaderContainer>
  );
};

PatientInfoHeader.displayName = 'PatientInfoHeader';
